/**
 * HTTP transport: `POST <baseUrl>/api` with one JSON envelope per request and a bearer token.
 * Server events are received by long polling `events.poll` while there are event listeners, after the seq of the
 * last event received (kept across token refreshes and poll restarts, so nothing is delivered twice); when the
 * server cannot replay what was missed it answers `gap` and the transport reports it to `onGap` listeners.
 * Status: 'reconnecting' while requests or polls fail at the network level (no response, a timeout, a proxy
 * error page), 'open' again as soon as the server answers.
 */
import type { EventEnvelope, RequestEnvelope, ResponseEnvelope } from '../../shared/protocol/envelope';
import type { EventBatch } from '../../shared/protocol/messages';
import { isResponseEnvelope } from '../../shared/protocol/envelope';
import { PROTOCOL_VERSION } from '../../shared/protocol/version';
import { DEFAULT_REQUEST_TIMEOUT_MS, Emitter, type SendOptions, type Transport, TransportError, type TransportStatus } from './transport';

export interface HttpTransportOptions {
  /** Backend origin, e.g. `https://fwp.example.org` (the transport appends `/api`). */
  baseUrl: string;
  fetch?: typeof fetch;
  timeoutMs?: number;
  /** Long-poll wait per `events.poll` request. */
  pollWaitMs?: number;
  /**
   * Automatic retries (same request id, so mutations stay idempotent) on network failures. Default 1; GameServices
   * passes 0 because BackendClient retries every request itself (two layers would double the time a call can take).
   */
  retries?: number;
}

export class HttpTransport implements Transport {
  readonly kind = 'http' as const;
  readonly connectionBound = false;
  private token: string | null = null;
  private readonly events = new Emitter<EventEnvelope>();
  private readonly status = new Emitter<TransportStatus>();
  private readonly gaps = new Emitter<{ after: number | null; last: number }>();
  /** Seq of the last event received (0 = none yet: the server starts at this client's first request). */
  private pollAfter = 0;
  private readonly fetchImpl: typeof fetch;
  private readonly endpoint: string;
  private closed = false;
  private polling = false;
  private pollGeneration = 0;
  private pollSeq = 0;
  private readonly inflight = new Set<AbortController>();
  private current: TransportStatus = 'connecting';

  constructor(private readonly opts: HttpTransportOptions) {
    this.fetchImpl = opts.fetch ?? ((...a: Parameters<typeof fetch>) => fetch(...a));
    this.endpoint = opts.baseUrl.replace(/\/+$/, '') + '/api';
  }

  async connect(): Promise<void> {
    this.setStatus('open');
  }

  private setStatus(s: TransportStatus): void {
    if (this.current === s || this.current === 'closed') return;
    this.current = s;
    this.status.emit(s);
  }

  private async post(request: RequestEnvelope, timeoutMs: number): Promise<ResponseEnvelope> {
    if (this.closed) throw new TransportError('Transport is closed', false);
    const ac = new AbortController();
    this.inflight.add(ac);
    const timer = setTimeout(() => ac.abort(), timeoutMs);
    try {
      const headers: Record<string, string> = { 'content-type': 'application/json' };
      if (this.token) headers.authorization = `Bearer ${this.token}`;
      let res: Response;
      try {
        res = await this.fetchImpl(this.endpoint, { method: 'POST', headers, body: JSON.stringify(request), signal: ac.signal });
      } catch (err) {
        if (!this.closed) this.setStatus('reconnecting');
        throw new TransportError(ac.signal.aborted ? 'Request timed out or was aborted' : `Network error: ${String(err)}`, !this.closed);
      }
      let body: unknown;
      try {
        body = await res.json();
      } catch {
        // A proxy in front of a stopped server answers with an error page (502/503/504).
        if (res.status >= 500) this.setStatus('reconnecting');
        throw new TransportError(`HTTP ${res.status}: response is not JSON`, res.status >= 500, res.status);
      }
      if (isResponseEnvelope(body)) {
        // UNAVAILABLE (a stopping server, or a dev proxy whose backend is down) is not a working connection.
        this.setStatus(!body.ok && body.error.code === 'UNAVAILABLE' ? 'reconnecting' : 'open');
        return body.id === '' ? { ...body, id: request.id } : body;
      }
      throw new TransportError(`HTTP ${res.status}: unexpected response`, res.status >= 500, res.status);
    } finally {
      clearTimeout(timer);
      this.inflight.delete(ac);
    }
  }

  async send(request: RequestEnvelope, opts: SendOptions = {}): Promise<ResponseEnvelope> {
    const timeout = opts.timeoutMs ?? this.opts.timeoutMs ?? DEFAULT_REQUEST_TIMEOUT_MS;
    let attempt = 0;
    for (;;) {
      try {
        return await this.post(request, timeout);
      } catch (err) {
        if (!(err instanceof TransportError) || !err.retryable || attempt >= (this.opts.retries ?? 1)) throw err;
        attempt++;
        await new Promise((r) => setTimeout(r, 200 * attempt));
      }
    }
  }

  onEvent(listener: (event: EventEnvelope) => void): () => void {
    const off = this.events.on(listener);
    this.ensurePolling();
    return () => {
      off();
    };
  }

  onStatus(listener: (status: TransportStatus) => void): () => void {
    return this.status.on(listener);
  }

  onGap(listener: (gap: { after: number | null; last: number }) => void): () => void {
    return this.gaps.on(listener);
  }

  resetEventPosition(): void {
    this.pollAfter = 0;
  }

  setAuthToken(token: string | null): void {
    if (token === this.token) return;
    this.token = token;
    this.pollGeneration++;
    this.polling = false;
    this.ensurePolling();
  }

  private ensurePolling(): void {
    if (this.polling || this.closed || !this.token || this.events.size === 0) return;
    this.polling = true;
    const gen = this.pollGeneration;
    void this.pollLoop(gen);
  }

  private async pollLoop(gen: number): Promise<void> {
    let backoff = 500;
    const wait = this.opts.pollWaitMs ?? 20_000;
    while (!this.closed && gen === this.pollGeneration && this.events.size > 0) {
      try {
        const after = this.pollAfter;
        const res = await this.post(
          { v: PROTOCOL_VERSION, id: `poll-${++this.pollSeq}`, type: 'events.poll', payload: { after, waitMs: wait } },
          wait + 10_000,
        );
        if (gen !== this.pollGeneration) return;
        if (!res.ok) {
          if (res.error.code === 'UNAUTHORIZED') break;
          await new Promise((r) => setTimeout(r, backoff));
          backoff = Math.min(backoff * 2, 10_000);
          continue;
        }
        backoff = 500;
        const result = res.result as EventBatch;
        // Events after `after` are gone (server buffer overflow or restart): report it, continue after `last`.
        if (result.gap || result.last < after) {
          this.pollAfter = result.last;
          this.gaps.emit({ after: after === 0 ? null : after, last: result.last });
          continue;
        }
        let next = after;
        for (const ev of result.events) {
          if (typeof ev.seq === 'number' && ev.seq <= next) continue; // already delivered
          if (typeof ev.seq === 'number') next = ev.seq;
          this.events.emit(ev);
        }
        this.pollAfter = Math.max(next, result.last);
      } catch {
        if (this.closed || gen !== this.pollGeneration) return;
        await new Promise((r) => setTimeout(r, backoff));
        backoff = Math.min(backoff * 2, 10_000);
      }
    }
    if (gen === this.pollGeneration) this.polling = false;
  }

  async close(): Promise<void> {
    if (this.closed) return;
    this.closed = true;
    this.pollGeneration++;
    for (const ac of this.inflight) ac.abort();
    this.setStatus('closed');
  }
}
